import localforage from 'localforage';
import { getRefreshPromise } from 'apollo/errorHandler';

const API_BASE_URL = process.env.REACT_APP_GRAPHQL_SERVER_BASE_URL || 'http://localhost';

const authHeaders = async () => {
  const token = await localforage.getItem('otx_a');
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

const handleResponse = async (response, context) => {
  if (response.ok) {
    return response.json();
  }
  // Un 401 aquí NO significa que la sesión esté muerta: lo normal es que el token de acceso haya
  // caducado y toque renovarlo, exactamente igual que hace Apollo.
  //
  // Antes esto vaciaba el localStorage entero y redirigía al login sin intentar nada. Además de
  // llevarse el tema y los borradores, competía con Apollo: si un 401 de pagos caía mientras Apollo
  // estaba renovando, se cerraba una sesión que acababa de renovarse. Se reutiliza la MISMA promesa
  // de renovación (Passport rota el refresh token, así que dos renovaciones en paralelo se anulan).
  // Si la renovación falla de verdad, errorHandler ya se encarga de mandar al login.
  if (response.status === 401) {
    await getRefreshPromise();
    throw new Error('Session expired');
  }
  const errorData = await response.json().catch(() => ({}));
  throw new Error(errorData.error || errorData.message || `Error en ${context}`);
};

/**
 * Detecta el gateway y moneda apropiados según la IP del usuario.
 * @returns {Promise<{ gateway: 'wompi', currency: 'COP'|'USD' }>}
 */
const detectCurrency = async () => {
  const response = await fetch(`${API_BASE_URL}/api/payment/detect-currency`, {
    headers: await authHeaders(),
  });
  return handleResponse(response, 'detectCurrency');
};

/**
 * Genera los datos para el widget de Wompi.
 * @param {{ plan_ids: number[] }} payload
 * @returns {Promise<{ reference, signature, amount_in_cents, public_key, currency }>}
 */
const createWompiTransaction = async (payload) => {
  const response = await fetch(`${API_BASE_URL}/api/wompi/create-transaction`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'createWompiTransaction');
};

/* ----------------------------- Suscripciones ------------------------------ */

/** URL de la API de Wompi según el prefijo de la llave pública (sandbox vs producción). */
const wompiBaseUrl = (publicKey) =>
  (publicKey || '').startsWith('pub_prod')
    ? 'https://production.wompi.co/v1'
    : 'https://sandbox.wompi.co/v1';

/**
 * Obtiene la llave pública y los tokens de aceptación necesarios para suscribirse.
 * @returns {Promise<{ acceptance_token, accept_personal_auth, public_key }>}
 */
const getAcceptanceTokens = async () => {
  const response = await fetch(`${API_BASE_URL}/api/wompi/acceptance-tokens`, {
    headers: await authHeaders(),
  });
  return handleResponse(response, 'getAcceptanceTokens');
};

/**
 * Tokeniza una tarjeta directamente contra Wompi con la llave PÚBLICA.
 * El PAN nunca pasa por nuestro backend (cumplimiento PCI).
 * @returns {Promise<{ id: string, brand, last_four, exp_month, exp_year }>}
 */
const tokenizeCard = async (publicKey, card) => {
  const response = await fetch(`${wompiBaseUrl(publicKey)}/tokens/cards`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${publicKey}`,
    },
    body: JSON.stringify({
      number: card.number,
      cvc: card.cvc,
      exp_month: card.exp_month,
      exp_year: card.exp_year,
      card_holder: card.card_holder,
    }),
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.status === 'ERROR' || !json?.data?.id) {
    const reason = json?.error?.messages
      ? Object.values(json.error.messages).flat().join(' ')
      : 'No pudimos validar la tarjeta. Revisa los datos.';
    throw new Error(reason);
  }
  return json.data;
};

/** Crea una suscripción. payload: { plan_id, card_token, acceptance_token, accept_personal_auth } */
const createSubscription = async (payload) => {
  const response = await fetch(`${API_BASE_URL}/api/wompi/subscriptions`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'createSubscription');
};

/** Obtiene la tarjeta guardada del usuario (payment_source reusable) o null. */
const getSavedCard = async () => {
  const response = await fetch(`${API_BASE_URL}/api/wompi/saved-card`, {
    headers: await authHeaders(),
  });
  return handleResponse(response, 'getSavedCard');
};

/** Obtiene la suscripción vigente del usuario (o null). */
const getMySubscription = async () => {
  const response = await fetch(`${API_BASE_URL}/api/wompi/subscriptions/me`, {
    headers: await authHeaders(),
  });
  return handleResponse(response, 'getMySubscription');
};

/**
 * Historial COMPLETO paginado de cobros de suscripción del usuario.
 * @returns {Promise<{ data: [], current_page, last_page, per_page, total }>}
 */
const getMySubscriptionCharges = async ({ page = 1, perPage = 25 } = {}) => {
  const response = await fetch(
    `${API_BASE_URL}/api/wompi/subscriptions/me/charges?page=${page}&per_page=${perPage}`,
    { headers: await authHeaders() },
  );
  return handleResponse(response, 'getMySubscriptionCharges');
};

/** Cancela una suscripción por id. */
const cancelSubscription = async (id) => {
  const response = await fetch(`${API_BASE_URL}/api/wompi/subscriptions/${id}/cancel`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse(response, 'cancelSubscription');
};

/** Actualiza la tarjeta de una suscripción. payload: { card_token, acceptance_token, accept_personal_auth } */
const updateSubscriptionCard = async (id, payload) => {
  const response = await fetch(`${API_BASE_URL}/api/wompi/subscriptions/${id}/update-card`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'updateSubscriptionCard');
};

/** Reintenta el cobro de la suscripción con la tarjeta ya registrada (mismo plástico). */
const retrySubscriptionPayment = async (id) => {
  const response = await fetch(`${API_BASE_URL}/api/wompi/subscriptions/${id}/retry`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse(response, 'retrySubscriptionPayment');
};

/* ------------------------- Stripe (suscripción USD) ----------------------- */

/**
 * Crea una sesión de Checkout de Stripe en modo suscripción y devuelve la URL hosteada.
 * Stripe maneja el cobro recurrente, reintentos, facturas y recibos.
 * @param {{ plan_id: number }} payload
 * @returns {Promise<{ url: string }>}
 */
const createStripeSubscriptionCheckout = async ({ plan_id: planId }) => {
  const currentUrl = window.location.href;
  const response = await fetch(`${API_BASE_URL}/api/stripe/subscription-checkout`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ plan_id: planId, success_url: currentUrl, cancel_url: currentUrl }),
  });
  return handleResponse(response, 'createStripeSubscriptionCheckout');
};

export default {
  detectCurrency,
  createWompiTransaction,
  getAcceptanceTokens,
  tokenizeCard,
  createSubscription,
  getSavedCard,
  getMySubscription,
  getMySubscriptionCharges,
  cancelSubscription,
  updateSubscriptionCard,
  retrySubscriptionPayment,
  createStripeSubscriptionCheckout,
};
